import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./styles.scss";
import CustomButton from "../../atoms/CustomButton";
import CircleAvatar from "../../atoms/CircleAvatar";
import { decodeCookieValue } from "../../../helpers/cookieHelper";
import { useCookies } from "react-cookie";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import LogoutModal from "../../organisms/modals/LogoutModal";
import { VolumeX, LogOut, UserRound, Bell } from "lucide-react";
import { useSelector } from "react-redux";
import IconCircle from "../../atoms/avatars/IconCircle";
import NotificationCreate from "./notification";

const Navbar = ({ scrollContainerRef }) => {
  const location = useLocation();
  const accessData = useSelector((state) => state.accessDetails?.accessData);
  const navigate = useNavigate();
  const [pageTitle, setPageTitle] = useState("");
  const [cookies] = useCookies(["e_n"]);
  const [isScrolled, setIsScrolled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null); // State for dropdown anchor
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false); // State for logout modal

  const pageTitles = {
    "/dashboard": "Home",
    "/dashboard/employees": "Employee Management",
    "/dashboard/teams": "Team Management",
    "/dashboard/settings": "Settings",
    "/dashboard/projects": "Projects",
    "/dashboard/tasks": "Tasks",
    "/dashboard/reports": "Reports",
    "/dashboard/notifications": "Notifications",
    "/dashboard/my-profile": "Profile Settings",
    "/contact": "Contact Us",
    "/dashboard/projects/projectinfo": "", 
    "/employee/dashboard": "Home",
    "/employee/dashboard/tasks": "Task Management",
    "/employee/dashboard/projects": "Projects",
    "/employee/dashboard/my-profile": "Profile Settings",
    "/dashboard/daily-status-report": "Daily Status Report",
    "/dashboard/team-status-report": "Team Status Report",
    "/dashboard/clientmanagement": "Client Management",
    "/dashboard/my-tasks":"Tasks"
  };

  useEffect(() => {
    // Get all path keys from pageTitles
    const pathKeys = Object.keys(pageTitles);

    // Sort keys by descending length to prioritize longer paths
    const sortedKeys = pathKeys.sort((a, b) => b.length - a.length);

    // Find the first key that the current path starts with
    const matchedKey = sortedKeys.find((key) =>
      location.pathname.startsWith(key)
    );

    // Get the base title from pageTitles or default to "Page"
    const baseTitle = matchedKey ? pageTitles[matchedKey] : "Page";

    // Check if there's a dynamic name in the state
    const dynamicName = location.state?.name;

    // Combine the base title with the dynamic name (if available)
    const finalTitle = dynamicName ? `${dynamicName}` : baseTitle;

    // Set the page title
    setPageTitle(finalTitle);
  }, [location.pathname, location.state]);

  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container) return;

    const handleScroll = () => {
      setIsScrolled(container.scrollTop > 10);
    };

    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [scrollContainerRef]);

  // Handle dropdown open
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle dropdown close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Open logout modal
  const handleOpenLogoutModal = () => {
    setIsLogoutModalOpen(true);
    handleMenuClose(); // Close the dropdown menu
  };

  // Close logout modal
  const handleCloseLogoutModal = () => {
    setIsLogoutModalOpen(false);
  };

  // Handle logout confirmation
  const handleLogoutConfirm = () => {
    navigate("/"); // Redirect to the login page
  };

  return (
    <>
      <nav className={`navbar-container ${isScrolled ? "scrolled" : ""}`}>
        {/* Static Left Section */}
        <div className="navbar-text h5">{pageTitle}</div>

        {/* Dynamic Right Section */}
        <div className="d-flex align-items-center gap-3">
        <NotificationCreate />
        <div
          onClick={handleMenuOpen}
          className="d-flex gap-2 align-items-center"
        >
          <CircleAvatar
            name={decodeCookieValue(cookies.e_n)}
            size={40}
            fontSize="15px"
          />
          <div className="cursor-pointer">
            <div style={{ fontWeight: 600, fontSize: "18px" }}>
              {decodeCookieValue(cookies.e_n)}
            </div>
            <div style={{ fontSize: "14px" }}>{accessData?.designation}</div>
          </div>
        </div>
        </div>

        {/* Dropdown Menu */}

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            style: {
              width: "250px",
              borderRadius: "8px",
              marginTop: "20px",
              fontFamily: "Nunito Sans, sans-serif",
              boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.15)",
            },
          }}
        >
          {/* Profile Section */}
          <div style={{ padding: "5px 16px 10px 16px" }}>
            <div
              onClick={handleMenuClose}
              className="d-flex gap-2 align-items-center cursor-pointer"
            >
              <CircleAvatar
                name={decodeCookieValue(cookies.e_n)}
                size={40}
                fontSize="14px"
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700 }}>
                  {decodeCookieValue(cookies.e_n)}
                </div>
                <Tooltip title={cookies.eml} arrow placement="bottom">
                  <Typography
                    variant="body2"
                    color="black"
                    className="email-text"
                  >
                    {cookies.eml}
                  </Typography>
                </Tooltip>
              </div>
            </div>
          </div>

          <Divider sx={{ backgroundColor: "#000000" }} />

          {/* Menu Items */}
          <div className="item-container">
            {/* Profile */}
            <MenuItem
              className="icon-item cursor-pointer"
              onClick={() => {
                navigate("/dashboard/my-profile");
                handleMenuClose();
              }}
              sx={{
                display: "flex",
                gap: 1,
                color: "#393939",
                padding: "12px 18px",
              }}
            >
              <UserRound size={20} color="#8F95B2" />
              Profile
            </MenuItem>

            {/* Logout */}
            <MenuItem
              className="icon-item cursor-pointer"
              onClick={handleOpenLogoutModal}
              sx={{ display: "flex", gap: 1, padding: "12px 18px" }}
            >
              <LogOut size={20} color="#8F95B2" />
              <span style={{ color: "#393939" }}>Log out</span>
            </MenuItem>
          </div>
        </Menu>
      </nav>
      <div>
        <LogoutModal
          isActive={isLogoutModalOpen}
          onClose={() => setIsLogoutModalOpen(false)}
        />
      </div>
    </>
  );
};

export default Navbar;
